import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme';

function SizeModal({ isOpen, onClose, onAdd, editingSize }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'standard',
  });

  useEffect(() => {
    if (editingSize && isOpen) {
      setFormData({
        name: editingSize.name || '',
        description: editingSize.description || '',
        type: editingSize.type || 'standard',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'standard',
      });
    }
  }, [editingSize, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Size name is required');
      return;
    }

    onAdd({
      id: editingSize?.id,
      name: formData.name,
      description: formData.description,
      type: formData.type,
    });

    setFormData({
      name: '',
      description: '',
      type: 'standard',
    });
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: spacing.lg,
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows['2xl'],
          padding: spacing.xl,
          width: '100%',
          maxWidth: '32rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.text.primary, margin: 0 }}>
            📏 {editingSize ? 'Edit Size' : 'Add New Size'}
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.text.secondary,
              padding: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </motion.button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
              Size Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., XL"
              required
              style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                fontSize: '0.95rem',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                e.target.style.borderColor = colors.primary[600];
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = colors.border;
              }}
            />
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
              Size Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                fontSize: '0.95rem',
                outline: 'none',
                cursor: 'pointer',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                e.target.style.borderColor = colors.primary[600];
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = colors.border;
              }}
            >
              <option value="standard">Standard (XS, S, M, L, XL...)</option>
              <option value="inch">Inch (1", 2", 3"...)</option>
              <option value="slipper">Slipper (5, 6, 7, 8, 9...)</option>
            </select>
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Size description..."
              rows="3"
              style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                fontSize: '0.95rem',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                e.target.style.borderColor = colors.primary[600];
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = colors.border;
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: spacing.lg }}>
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                flex: 1,
                backgroundColor: colors.primary[500],
                color: 'white',
                padding: `${spacing.md} ${spacing.lg}`,
                borderRadius: '0.75rem',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[600];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[500];
              }}
            >
              {editingSize ? '✓ Update Size' : '✓ Add Size'}
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              style={{
                flex: 1,
                backgroundColor: colors.text.tertiary,
                color: colors.text.primary,
                padding: `${spacing.md} ${spacing.lg}`,
                borderRadius: '0.75rem',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default SizeModal;
